/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { ArrowUpRight, ArrowDownRight, Activity, DollarSign, Users, Wallet, ArrowDown } from 'lucide-react';
import { AreaChart } from '../../charts/AreaChart';

export const DashboardPreview = () => {
  // Static Data for Preview
  const revenueData = [30, 40, 45, 50, 49, 60, 70, 91, 120, 100, 110, 130];
  const metrics = [
      { label: "Total Revenue", value: "$452,310", change: 12.5, trend: "up", icon: DollarSign },
      { label: "Active Clients", value: "2,345", change: 8.1, trend: "up", icon: Users },
      { label: "Tx Volume", value: "12,450", change: -2.4, trend: "down", icon: Activity },
      { label: "Net Profit", value: "$384,463", change: 14.2, trend: "up", icon: Wallet },
  ];

  const transactions = [
      { id: 'TX_9921', type: 'payment', amount: 150.00, asset: 'USDC', status: 'completed', date: 'Just now', hash: '8x92...3k1' },
      { id: 'TX_9920', type: 'swap', amount: 450.50, asset: 'SOL', status: 'completed', date: '2 min ago', hash: '4k21...9x2' },
      { id: 'TX_9919', type: 'escrow', amount: 1200.00, asset: 'SUI', status: 'pending', date: '15 min ago', hash: '9l10...2m4' },
      { id: 'TX_9918', type: 'payment', amount: 85.00, asset: 'USDC', status: 'completed', date: '1 hour ago', hash: '3p55...1q0' },
  ];

  return (
    <div className="w-full aspect-[16/10] bg-[#F8F9FA] dark:bg-[#0A0B0F] rounded-xl border border-stone-200 dark:border-white/10 overflow-hidden flex flex-col shadow-2xl relative select-none">
        {/* Fake Browser Header */}
        <div className="h-10 border-b border-stone-200 dark:border-white/10 flex items-center px-4 justify-between bg-white/80 dark:bg-[#15171C]/90 backdrop-blur shrink-0">
            <div className="flex items-center gap-2">
               <div className="w-2.5 h-2.5 rounded-full bg-red-500/20 border border-red-500/50"></div>
               <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/20 border border-yellow-500/50"></div>
               <div className="w-2.5 h-2.5 rounded-full bg-green-500/20 border border-green-500/50"></div>
            </div>
            <div className="h-6 px-3 bg-stone-100 dark:bg-white/10 rounded-md flex items-center justify-center text-[10px] text-stone-500 font-mono">
                ghostpay.fun/dashboard
            </div>
        </div>

        {/* Dashboard Content */}
        <div className="p-6 flex-1 flex flex-col gap-6 overflow-hidden relative">
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/0 to-white/5 pointer-events-none"></div>
            
            {/* Header */}
            <div className="flex justify-between items-end shrink-0">
                <div>
                    <h2 className="font-serif text-2xl text-[#0A0B0F] dark:text-white">Dashboard</h2>
                    <p className="text-xs text-stone-500">Real-time overview.</p>
                </div>
                <div className="flex gap-2">
                    <div className="px-3 py-1.5 bg-stone-900 dark:bg-white text-white dark:text-black rounded-lg text-xs font-bold shadow-lg">Generate Report</div>
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 shrink-0">
                {metrics.map((m, i) => (
                    <div key={i} className="bg-white dark:bg-[#15171C] p-4 rounded-xl border border-stone-100 dark:border-white/5 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                            <div className="p-2 bg-stone-100 dark:bg-white/5 rounded-lg text-[#0A0B0F] dark:text-white">
                                <m.icon size={16} />
                            </div>
                            <div className={`flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${m.trend === 'up' ? 'text-green-600 bg-green-50 dark:bg-green-900/20' : 'text-red-600 bg-red-50 dark:bg-red-900/20'}`}>
                                {m.trend === 'up' ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
                                {Math.abs(m.change)}%
                            </div>
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-1">{m.label}</div>
                        <div className="text-xl font-serif text-[#0A0B0F] dark:text-white font-bold">{m.value}</div>
                    </div>
                ))}
            </div>

            <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                {/* Chart Area */}
                <div className="flex-1 bg-white dark:bg-[#15171C] rounded-xl border border-stone-100 dark:border-white/5 p-5 flex flex-col min-h-0">
                    <div className="flex justify-between items-center mb-4 shrink-0">
                        <h3 className="font-serif text-lg text-[#0A0B0F] dark:text-white">Revenue</h3>
                        <div className="text-xs font-mono text-stone-400">Last 12 Months</div>
                    </div>
                    <div className="flex-1 w-full min-h-0 relative">
                        <div className="absolute inset-0">
                            <AreaChart data={revenueData} />
                        </div>
                    </div>
                </div>

                {/* Recent Transactions */}
                <div className="w-full lg:w-1/3 bg-white dark:bg-[#15171C] rounded-xl border border-stone-100 dark:border-white/5 p-5 flex flex-col min-h-0">
                    <h3 className="font-serif text-lg text-[#0A0B0F] dark:text-white mb-4 shrink-0">Live Feed</h3>
                    <div className="flex-1 space-y-3 overflow-hidden relative">
                        <div className="absolute inset-0 overflow-y-auto no-scrollbar">
                            <div className="space-y-3">
                                {transactions.map((tx, i) => (
                                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-stone-50 dark:bg-white/[0.02]">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>
                                            {tx.status === 'completed' ? <ArrowDown size={14}/> : <Activity size={14}/>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold text-[#0A0B0F] dark:text-white truncate">{tx.id}</div>
                                            <div className="text-[10px] text-stone-500">{tx.date}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs font-mono font-bold text-[#0A0B0F] dark:text-white">+${tx.amount}</div>
                                            <div className="text-[10px] text-stone-400">{tx.asset}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
}